import {notNull} from "./assert";

enum Orientation {
    Landscape = 0,
    Portrait = 1,
}

const landscapeAspectRatioMin = 1.0;
const viewportIntersectionRootMarginFirstView = '100px';
const viewportIntersectionRootMarginDefault = '-100px';

const videoViewportFirstLoadObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
        const videoBanner = entry.target as VideoBanner;
        if (entry.isIntersecting) {
            videoBanner.onViewportEnter();
        }
    });
}, {
    rootMargin: viewportIntersectionRootMarginFirstView,
});

const videoViewportObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
        const videoBanner = entry.target as VideoBanner;
        if (entry.isIntersecting) {
            videoBanner.onViewportEnter();
        } else {
            videoBanner.onViewportExit();
        }
    });
}, {
    rootMargin: viewportIntersectionRootMarginDefault,
});

export class VideoBanner extends HTMLElement {
    private videoActive?: HTMLVideoElement;
    private readonly videoLandscape: HTMLVideoElement;
    private readonly videoPortrait: HTMLVideoElement;
    private readonly content: HTMLElement|null;
    private readonly playPauseButton: HTMLButtonElement;
    private orientation?: Orientation;
    private orientationChangedTimestamp: number;
    private orientationNew: Orientation|null = null;
    private orientationChangeTimeout: number|null = null;
    private pauseRequested: boolean;
    private visibleInViewport = false;
    private visibleInViewportAtLeastOnce = false;
    private initializedAtLeastOnce = false;
    private readonly windowResizeEventHandler: (...args: any[]) => any;
    private readonly documentVisibilityEventHandler: (...args: any[]) => any;

    public constructor() {
        super();
        this.pauseRequested = this.dataset.clxAutoplay !== 'true';
        this.playPauseButton = notNull(this.querySelector<HTMLButtonElement>('.video-banner__play-pause-button'));
        this.videoLandscape = notNull(this.querySelector<HTMLVideoElement>('video.video-banner__video--landscape'));
        this.videoPortrait = notNull(this.querySelector<HTMLVideoElement>('video.video-banner__video--portrait'));
        this.content = this.querySelector<HTMLElement>('.video-banner__content');
        this.orientationChangedTimestamp = Date.now();
        this.windowResizeEventHandler = this.onWindowResize.bind(this);
        this.documentVisibilityEventHandler = this.onDocumentVisibilityChange.bind(this);
    }

    public connectedCallback() {
        if (! this.initializedAtLeastOnce) {
            this.initManualPauseOnce();
            this.initializedAtLeastOnce = true;
        }

        this.orientation = this.getComputedOrientation();
        if (this.orientation === Orientation.Landscape) {
            this.videoActive = this.videoLandscape;
        } else {
            this.videoActive = this.videoPortrait;
        }

        window.addEventListener('resize', this.windowResizeEventHandler, { passive: true });
        document.addEventListener('visibilitychange', this.documentVisibilityEventHandler);

        videoViewportObserver.observe(this);
        if (! this.visibleInViewportAtLeastOnce) {
            videoViewportFirstLoadObserver.observe(this);
        }
    }

    public disconnectedCallback() {
        videoViewportFirstLoadObserver.unobserve(this);
        videoViewportObserver.unobserve(this);

        window.removeEventListener('resize', this.windowResizeEventHandler);
        document.removeEventListener('visibilitychange', this.documentVisibilityEventHandler);

        const videoActive = this.videoActive;

        this.videoActive = undefined;
        this.orientation = undefined;
        this.orientationNew = null;
        if (this.orientationChangeTimeout !== null) {
            clearTimeout(this.orientationChangeTimeout);
            this.orientationChangeTimeout = null;
        }
        this.visibleInViewport = false;

        videoActive?.pause();
    }

    public onViewportEnter() {
        videoViewportFirstLoadObserver.unobserve(this);

        this.visibleInViewport = true
        this.visibleInViewportAtLeastOnce = true;

        if (! this.pauseRequested) {
            this.play();
        }
    }

    public onViewportExit() {
        this.visibleInViewport = false
        this.pause();
    }

    private pause() {
        this.videoActive?.pause();
        this.classList.add('video-banner--paused');
        this.classList.remove('video-banner--playing');
    }

    private play() {
        if (this.videoActive) {
            playVideoIgnoreAbort(this.videoActive);
        }
        this.classList.add('video-banner--playing');
        this.classList.remove('video-banner--paused');
    }

    private initManualPauseOnce() {
        this.playPauseButton.addEventListener('click', e => {
            e.preventDefault();
            this.togglePlayPause();
        });
    }

    private togglePlayPause() {
        this.pauseRequested = ! this.pauseRequested;
        if (this.pauseRequested) {
            this.pause();
        } else {
            this.play();
        }
    }

    private onDocumentVisibilityChange() {
        /**
         * Das Video wird pausiert ohne das Pause-Overlay einzublenden,
         * weil das Dokument sowieso nicht sichtbar ist. Dadurch kommt
         * es nicht zu einem Flickern des Pause-Overlays.
         */
        if (document.visibilityState === 'visible') {
            if (! this.pauseRequested && this.visibleInViewport) {
                if (this.videoActive) {
                    playVideoIgnoreAbort(this.videoActive);
                }
            }
        } else {
            this.videoActive?.pause();
        }
    }

    private onWindowResize() {
        this.invalidateOrientation();
    }

    private invalidateOrientation() {
        const aspectRatio = window.innerWidth / window.innerHeight;
        const orientation = aspectRatio > landscapeAspectRatioMin ? Orientation.Landscape : Orientation.Portrait;

        if (this.orientation === orientation) {
            return;
        }
        this.orientationNew = orientation;
        if (this.orientationChangeTimeout !== null) {
            return;
        }

        const msSinceLastMediaSwitch = Date.now() - this.orientationChangedTimestamp;
        const timeout = msSinceLastMediaSwitch < 1000 ? 1000 : 0;

        this.orientationChangeTimeout = setTimeout(() => {
            const orientationNew = this.orientationNew;

            this.orientationNew = null;
            this.orientationChangeTimeout = null;

            if (orientationNew === null || orientationNew === this.orientation) {
                return;
            }

            this.orientation = orientationNew;

            if (this.videoActive) {
                this.videoActive.pause();
                this.videoActive.setAttribute('aria-hidden', 'true');
            }

            if (this.orientation === Orientation.Landscape) {
                this.videoActive = this.videoLandscape;
            } else {
                this.videoActive = this.videoPortrait;
            }

            this.videoActive.removeAttribute('aria-hidden');
            if (! this.pauseRequested && this.visibleInViewport) {
                if (this.videoActive) {
                    playVideoIgnoreAbort(this.videoActive);
                }
            } else {
                this.videoActive.pause();
            }

            this.orientationChangedTimestamp = Date.now();
        }, timeout);
    }

    private getComputedOrientation() {
        const landscapeVisible = getComputedStyle(this.videoLandscape).display !== 'none';
        return landscapeVisible ? Orientation.Landscape : Orientation.Portrait;
    }
}

async function playVideoIgnoreAbort(video: HTMLVideoElement): Promise<void> {
    try {
        await video.play();
    } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
            // Ignore, because video was paused.
            return;
        }
        throw e;
    }
}

if (! customElements.get('clx-video-banner')) {
    customElements.define('clx-video-banner', VideoBanner);
}
